// src/components/coach/BroadcastSmsModal.jsx
// Compose + confirm UI for a coach's SMS broadcast to a team roster.
// Backend: functions/modules/coachBroadcast.js (callable: sendCoachBroadcastSms).
//
// Responsibilities here:
//   • Show how many parents on the current roster will actually receive
//     the text (pre-filtered by smsOptIn + valid phone). This manages
//     expectations — if the count is 0, the coach knows why before paying
//     a round-trip to the server.
//   • Enforce the same 280-char coach-body cap the server applies, so the
//     user gets live feedback rather than a cryptic "invalid-argument".
//   • Preview the appended "Reply STOP to unsubscribe." footer so there
//     are no surprises about the on-wire text.
//   • Double-confirm the urgent toggle — urgent bypasses quiet hours and
//     should never be the default path.
'use client';

import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import toast from 'react-hot-toast';
import Modal from '@/components/Modal';
import { Button } from '@/components/button';
import {
  ChatBubbleLeftRightIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ClockIcon,
  CheckBadgeIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { isValidUsE164 } from '@/utils/phone';

// Must match MAX_COACH_BODY in coachBroadcast.js.
const MAX_COACH_BODY = 280;
const STOP_FOOTER = '\n\nReply STOP to unsubscribe.';

/**
 * Human-friendly "time ago" for broadcast history rows. Deliberately
 * short ("2h ago", "3d ago") — the modal is cramped and exact timestamps
 * aren't useful at a glance. Locale-aware would be nicer but we don't
 * have a date-fns formatDistance helper imported here yet.
 */
function timeAgo(iso, locale = 'en') {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  const L = locale === 'es'
    ? { s: 'seg', m: 'min', h: 'h', d: 'd', just: 'recién' }
    : { s: 's',   m: 'm',   h: 'h', d: 'd', just: 'just now' };
  if (secs < 10) return L.just;
  if (secs < 60) return `${secs}${L.s}`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}${L.m}`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}${L.h}`;
  const days = Math.floor(hrs / 24);
  return `${days}${L.d}`;
}

/** Per-status color tokens for the history row pill. */
function statusStyle(status) {
  switch (status) {
    case 'sent':           return 'bg-emerald-100 text-emerald-700';
    case 'partial':        return 'bg-amber-100 text-amber-800';
    case 'failed':         return 'bg-red-100 text-red-700';
    case 'no_recipients':  return 'bg-slate-200 text-slate-600';
    case 'sending':        return 'bg-blue-100 text-blue-700';
    default:               return 'bg-slate-200 text-slate-600';
  }
}

export default function BroadcastSmsModal({
  open,
  onClose,
  teamId,
  seasonId = null,
  registrations = [],   // active roster used to preview eligibility count
  strings,              // bilingual labels from parent page
}) {
  const [message, setMessage]   = useState('');
  const [urgent, setUrgent]     = useState(false);
  const [sending, setSending]   = useState(false);

  // Broadcast history (lazy-loaded on open). Kept to last 5 by default —
  // enough for a coach to glance at "did Tuesday's cancellation go out?"
  // without turning the modal into a separate reporting page.
  const [history, setHistory]         = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError]     = useState(null);

  const loadHistory = useCallback(async () => {
    if (!teamId) return;
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const fn = httpsCallable(getFunctions(), 'listTeamBroadcasts');
      const res = await fn({ teamId, limit: 5 });
      setHistory(Array.isArray(res?.data?.broadcasts) ? res.data.broadcasts : []);
    } catch (err) {
      setHistoryError(err?.message || 'Could not load history.');
    } finally {
      setHistoryLoading(false);
    }
  }, [teamId]);

  // Fetch when the modal opens OR after a successful send (history is
  // reloaded inside handleSend so the just-sent broadcast appears at top).
  useEffect(() => {
    if (open) loadHistory();
  }, [open, loadHistory]);

  // Eligibility preview — mirrors the server's filter (smsOptIn===true
  // + parseable E.164 phone + not cancelled). Close to correct; the
  // server does a fresh roster read on submit, so this is purely for UX.
  const eligibleCount = useMemo(() => {
    if (!Array.isArray(registrations)) return 0;
    return registrations.filter((r) => (
      r.status !== 'cancelled' &&
      r.smsOptIn === true &&
      isValidUsE164(r.parentPhone || '')
    )).length;
  }, [registrations]);

  const trimmed = message.trim();
  const overLimit = trimmed.length > MAX_COACH_BODY;
  const canSend = !sending && trimmed.length > 0 && !overLimit && eligibleCount > 0;

  const handleSend = async () => {
    if (!canSend) return;
    if (!teamId) { toast.error('Missing team context.'); return; }

    // Last-chance guard for urgent broadcasts — this bypasses quiet hours,
    // which is a TCPA liability if used casually.
    if (urgent) {
      const ok = window.confirm(strings.confirmUrgent);
      if (!ok) return;
    }

    setSending(true);
    try {
      const fn = httpsCallable(getFunctions(), 'sendCoachBroadcastSms');
      const res = await fn({
        teamId,
        seasonId: seasonId || null,
        message: trimmed,
        urgent: !!urgent,
      });
      const { attempted = 0, sent = 0, failed = 0 } = res?.data || {};
      if (attempted === 0) {
        toast(strings.toastNoRecipients, { icon: '📵' });
      } else if (failed === 0) {
        toast.success(strings.toastSent.replace('{n}', String(sent)));
      } else {
        toast(strings.toastPartial
          .replace('{sent}', String(sent))
          .replace('{failed}', String(failed)),
          { icon: '⚠️' });
      }
      // Reset the compose state but keep the modal open so the coach sees
      // their new entry at the top of "Recent broadcasts". This doubles
      // as confirmation-of-send without needing a separate success screen.
      setMessage('');
      setUrgent(false);
      loadHistory();
    } catch (err) {
      const code = err?.code || '';
      const msg = err?.message || strings.toastGenericError;
      if (code === 'functions/resource-exhausted') {
        toast.error(msg); // rate-limit copy is informative, show it verbatim
      } else if (code === 'functions/permission-denied') {
        toast.error(strings.toastForbidden);
      } else {
        toast.error(msg);
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal open={open} onClose={sending ? () => {} : onClose} title={strings.title}>
      <div className="space-y-4">
        {/* Eligibility preview */}
        <div className="flex items-center justify-between rounded-lg bg-slate-50 dark:bg-zinc-800 p-3 text-sm">
          <span className="text-slate-600 dark:text-zinc-300">
            {strings.recipientsLabel}
          </span>
          <span className={eligibleCount === 0 ? 'text-red-600 font-semibold' : 'text-slate-900 dark:text-white font-semibold'}>
            {eligibleCount}
          </span>
        </div>
        {eligibleCount === 0 && (
          <p className="text-xs text-slate-500 dark:text-zinc-400 leading-relaxed">
            {strings.noEligibleHint}
          </p>
        )}

        {/* Textarea */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-zinc-200 mb-1">
            {strings.messageLabel}
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
            disabled={sending}
            maxLength={MAX_COACH_BODY + 50}  // soft cap — server enforces hard cap
            placeholder={strings.messagePlaceholder}
            className="block w-full rounded-lg border border-slate-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-2 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
          />
          <div className="mt-1 flex items-center justify-between text-xs">
            <span className={overLimit ? 'text-red-600' : 'text-slate-500 dark:text-zinc-400'}>
              {trimmed.length} / {MAX_COACH_BODY}
            </span>
            {overLimit && (
              <span className="text-red-600 font-medium">{strings.tooLong}</span>
            )}
          </div>
        </div>

        {/* STOP footer preview */}
        <div className="rounded-lg border border-slate-200 dark:border-zinc-700 bg-slate-50 dark:bg-zinc-800 p-3 text-xs text-slate-500 dark:text-zinc-400">
          <div className="font-medium text-slate-600 dark:text-zinc-300 mb-1">
            {strings.footerPreviewLabel}
          </div>
          <div className="font-mono whitespace-pre-wrap">{STOP_FOOTER.trim()}</div>
        </div>

        {/* Urgent toggle */}
        <label className="flex items-start gap-2.5 cursor-pointer select-none rounded-lg border border-slate-200 dark:border-zinc-700 p-3">
          <input
            type="checkbox"
            checked={urgent}
            onChange={(e) => setUrgent(e.target.checked)}
            disabled={sending}
            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
          />
          <span className="text-sm leading-snug">
            <span className="flex items-center gap-1.5 font-medium text-slate-700 dark:text-zinc-200">
              <ExclamationTriangleIcon className="h-4 w-4 text-amber-500" />
              {strings.urgentLabel}
            </span>
            <span className="block text-xs text-slate-500 dark:text-zinc-400 mt-0.5">
              {strings.urgentHint}
            </span>
          </span>
        </label>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button outline onClick={onClose} disabled={sending}>
            {strings.cancel}
          </Button>
          <Button color="blue" onClick={handleSend} disabled={!canSend}>
            <ChatBubbleLeftRightIcon className="h-4 w-4 mr-1.5" />
            {sending ? strings.sending : strings.send}
          </Button>
        </div>

        {/* Recent broadcasts — lazy-loaded when modal opens. Kept as a
           compact list rather than a full report so this modal stays
           single-purpose. */}
        <div className="border-t border-slate-200 dark:border-zinc-700 pt-3 mt-2">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-zinc-400">
              {strings.historyTitle}
            </h4>
            <button
              type="button"
              onClick={loadHistory}
              disabled={historyLoading}
              className="text-xs text-slate-500 dark:text-zinc-400 hover:text-slate-700 dark:hover:text-zinc-200 inline-flex items-center gap-1 disabled:opacity-50"
              title={strings.historyRefresh}
            >
              <ArrowPathIcon className={historyLoading ? 'h-3.5 w-3.5 animate-spin' : 'h-3.5 w-3.5'} />
            </button>
          </div>
          {historyError ? (
            <p className="text-xs text-red-600 flex items-center gap-1">
              <XCircleIcon className="h-3.5 w-3.5" />
              {historyError}
            </p>
          ) : historyLoading && history.length === 0 ? (
            <p className="text-xs text-slate-400 dark:text-zinc-500">{strings.historyLoading}</p>
          ) : history.length === 0 ? (
            <p className="text-xs text-slate-400 dark:text-zinc-500">{strings.historyEmpty}</p>
          ) : (
            <ul className="space-y-1.5">
              {history.map((b) => (
                <li
                  key={b.id}
                  className="rounded-md bg-slate-50 dark:bg-zinc-800 px-2.5 py-1.5 text-xs"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-slate-500 dark:text-zinc-400 inline-flex items-center gap-1">
                      <ClockIcon className="h-3 w-3" />
                      {timeAgo(b.createdAt, strings._locale)}
                      {b.urgent && (
                        <span className="ml-1 rounded bg-amber-100 text-amber-800 px-1 py-px text-[10px] font-medium uppercase">
                          {strings.historyUrgentBadge}
                        </span>
                      )}
                    </span>
                    <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase ${statusStyle(b.status)}`}>
                      {strings.historyStatusLabels?.[b.status] || b.status}
                    </span>
                  </div>
                  <div className="mt-1 text-slate-700 dark:text-zinc-200 line-clamp-2">
                    {b.body}
                  </div>
                  <div className="mt-1 text-[11px] text-slate-500 dark:text-zinc-400 inline-flex items-center gap-1">
                    <CheckBadgeIcon className="h-3 w-3" />
                    {strings.historyCounts
                      .replace('{sent}', String(b.sentCount))
                      .replace('{total}', String(b.recipientCount))}
                    {b.failedCount > 0 && (
                      <span className="text-red-600">
                        · {strings.historyFailedSuffix.replace('{n}', String(b.failedCount))}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  );
}
